import { useTranslation } from 'react-i18next'
import { MarketingPage } from '@/components/marketing/marketing-page'
import { ProseSection, ProseParagraphs } from '@/components/marketing/prose-section'
import { ComparisonTable, type ComparisonRow } from '@/components/marketing/comparison-table'
import { MarketingCta } from '@/components/marketing/marketing-cta'
import { RelatedLinks } from '@/components/marketing/related-links'
import { FaqSection } from '@/components/marketing/faq-section'
import { buildRelatedLinks, type MarketingPageKey } from '@/components/marketing/links'
import { arrayKey, objectArrayKey } from '@/components/marketing/i18n-array'
import { JsonLd } from '@/components/seo/json-ld'
import {
  breadcrumbSchema,
  articleSchema,
  faqSchema,
} from '@/components/marketing/marketing-schema'

interface ComparePageProps {
  /** i18n sub-key, e.g. "comparePerplexity". */
  readonly contentKey: MarketingPageKey
  readonly path: string
  /** The competitor's display name for the comparison table column. */
  readonly otherLabel: string
  readonly relatedKeys: readonly MarketingPageKey[]
}

/**
 * Shared template for /compare/* pages. The body, table rows, and SEO all come
 * from the page's `marketing.<contentKey>.*` i18n keys, so a new comparison page
 * is just new copy plus a route — no new component.
 */
export function ComparePage({ contentKey, path, otherLabel, relatedKeys }: ComparePageProps) {
  const { t, i18n } = useTranslation()
  const body = (name: string) => arrayKey(t, `marketing.${contentKey}.${name}.body`)
  const rows = objectArrayKey<ComparisonRow>(t, `marketing.${contentKey}.table.rows`)
  const faq = faqSchema(t, contentKey)

  return (
    <MarketingPage
      seoTitle={t(`marketing.${contentKey}.seo.title`)}
      seoDescription={t(`marketing.${contentKey}.seo.description`)}
      path={path}
      eyebrow={t(`marketing.${contentKey}.eyebrow`)}
      heading={t(`marketing.${contentKey}.h1`)}
      lead={t(`marketing.${contentKey}.lead`)}
    >
      <JsonLd data={breadcrumbSchema(contentKey, [{ name: 'Compare', path: '/' }])} />
      <JsonLd
        data={articleSchema({
          headline: t(`marketing.${contentKey}.h1`),
          description: t(`marketing.${contentKey}.seo.description`),
          path,
          language: i18n.language,
        })}
      />
      {faq && <JsonLd data={faq} />}

      <ProseSection title={t(`marketing.${contentKey}.intro.title`)}>
        <ProseParagraphs paragraphs={body('intro')} />
      </ProseSection>

      <section className="mx-auto max-w-3xl px-6 py-10">
        <h2 className="mb-6 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {t(`marketing.${contentKey}.table.title`)}
        </h2>
        <ComparisonTable
          mindwebLabel={t('marketing.common.mindwebLabel')}
          otherLabel={otherLabel}
          rows={rows}
        />
      </section>

      <ProseSection title={t(`marketing.${contentKey}.whenOther.title`)}>
        <ProseParagraphs paragraphs={body('whenOther')} />
      </ProseSection>
      <ProseSection title={t(`marketing.${contentKey}.whenMindweb.title`)}>
        <ProseParagraphs paragraphs={body('whenMindweb')} />
      </ProseSection>

      <FaqSection contentKey={contentKey} />

      <MarketingCta
        title={t(`marketing.${contentKey}.cta.title`)}
        body={t(`marketing.${contentKey}.cta.body`)}
        buttonLabel={t(`marketing.${contentKey}.cta.button`)}
        to="/"
      />
      <RelatedLinks
        heading={t('marketing.common.relatedHeading')}
        links={buildRelatedLinks(t, contentKey, relatedKeys)}
      />
    </MarketingPage>
  )
}
